import { useCallback, useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { fetchLegalDocumentAcceptances } from '@/lib/api/legal-document-acceptances-admin.service'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { ApiClientError } from '@/lib/api/client'
import type {
  LegalDocumentAcceptanceRow,
  LegalDocumentAcceptancesPagination,
} from '@/types/legal-documents'
import styles from './LegalDocumentAcceptancesPage.module.css'

const PER_PAGE = 20
const SEARCH_INPUT_ID = 'legal-acceptances-search'

function formatDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('pt-BR')
}

function truncateUserAgent(value: string | null): string {
  if (!value) return '—'
  return value.length > 80 ? `${value.slice(0, 77)}…` : value
}

export function LegalDocumentAcceptancesPage() {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const [rows, setRows] = useState<LegalDocumentAcceptanceRow[]>([])
  const [pagination, setPagination] = useState<LegalDocumentAcceptancesPagination | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 400)
    return () => window.clearTimeout(id)
  }, [searchInput])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, dateFrom, dateTo])

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await runWithStepUp(() =>
        fetchLegalDocumentAcceptances({
          search: debouncedSearch || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          page,
          per_page: PER_PAGE,
        })
      )
      setRows(response.items)
      setPagination(response.pagination)
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Erro ao carregar aceites.'
      )
      setRows([])
      setPagination(null)
    } finally {
      setIsLoading(false)
    }
  }, [debouncedSearch, dateFrom, dateTo, page, runWithStepUp])

  useEffect(() => {
    void load()
  }, [load])

  const showFullSpinner = isLoading && rows.length === 0
  const totalLabel = pagination
    ? `${pagination.total} registro${pagination.total === 1 ? '' : 's'}`
    : null

  return (
    <div className={styles.page}>
      <p className={styles.hint}>
        Aceites dos <strong>Termos da Plataforma</strong> por instituições e colaboradores.
      </p>

      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <label className={styles.fieldLabel} htmlFor={SEARCH_INPUT_ID}>
            Nome ou e-mail
          </label>
          <div className={styles.searchField}>
            <Icon icon="mdi:magnify" className={styles.searchIcon} aria-hidden />
            <input
              id={SEARCH_INPUT_ID}
              type="search"
              className={styles.searchInput}
              placeholder="Buscar usuário ou instituição…"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <div className={styles.dateWrap}>
          <label className={styles.fieldLabel} htmlFor="legal-acceptances-date-from">
            Data inicial
          </label>
          <input
            id="legal-acceptances-date-from"
            type="date"
            className={styles.dateInput}
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
          />
        </div>

        <div className={styles.dateWrap}>
          <label className={styles.fieldLabel} htmlFor="legal-acceptances-date-to">
            Data final
          </label>
          <input
            id="legal-acceptances-date-to"
            type="date"
            className={styles.dateInput}
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
          />
        </div>

        {(dateFrom || dateTo || debouncedSearch) && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={() => {
              setSearchInput('')
              setDateFrom('')
              setDateTo('')
            }}
          >
            Limpar filtros
          </button>
        )}
      </div>

      {showFullSpinner ? (
        <p className={styles.feedback}>Carregando aceites…</p>
      ) : error ? (
        <div className={styles.feedbackBlock}>
          <p className={styles.error} role="alert">
            {error}
          </p>
          <button type="button" className={styles.retryButton} onClick={() => void load()}>
            Tentar novamente
          </button>
        </div>
      ) : rows.length === 0 ? (
        <p className={styles.feedback}>Nenhum aceite encontrado com os filtros aplicados.</p>
      ) : (
        <div className={styles.tableCard}>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>E-mail</th>
                  <th>Tipo</th>
                  <th>Instituição</th>
                  <th>Versão</th>
                  <th>Data do aceite</th>
                  <th>IP</th>
                  <th>Navegador</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className={styles.nameCell}>{row.name}</td>
                    <td>{row.email}</td>
                    <td>
                      <span
                        className={
                          row.acceptor_type === 'collaborator'
                            ? styles.badgeCollaborator
                            : styles.badgeInstitution
                        }
                      >
                        {row.acceptor_type_label}
                      </span>
                    </td>
                    <td>{row.institution_name}</td>
                    <td>
                      <span className={styles.versionTag}>v{row.document_version}</span>
                    </td>
                    <td>{formatDateTime(row.accepted_at)}</td>
                    <td>{row.accepted_ip ?? '—'}</td>
                    <td className={styles.uaCell} title={row.accepted_user_agent ?? undefined}>
                      {truncateUserAgent(row.accepted_user_agent)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && (
            <footer className={styles.tableFooter}>
              <span className={styles.paginationMeta}>
                {totalLabel}
                {pagination.last_page > 1
                  ? ` · Página ${pagination.current_page} de ${pagination.last_page}`
                  : ''}
              </span>
              {pagination.last_page > 1 && (
                <div className={styles.pagination}>
                  <button
                    type="button"
                    className={styles.pageBtn}
                    disabled={pagination.current_page <= 1 || isLoading}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    className={styles.pageBtn}
                    disabled={pagination.current_page >= pagination.last_page || isLoading}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Próxima
                  </button>
                </div>
              )}
            </footer>
          )}
        </div>
      )}
    </div>
  )
}
